// Percorso file: src/pages/TeamsList.jsx
//
// TEAMS — pagina di SOLA CONSULTAZIONE per ADMIN e SUPERADMIN (cantiere
// TEAMS-LIST, E1, 25/9/2026). Base: docs/cantieri/teams-list/RICOG-TEAMS-LIST-R1.md.
//
// DUE PAGINE, UN FILE (dichiarato nel referto E1): l'elenco e il dettaglio
// condividono il caricamento e la regola «chi sta in quella squadra», e
// tenerli insieme evita di scriverla due volte.
//   - /teams-list                   → `TeamsList` (default): le squadre
//     dell'annata guardata col numero dei giocatori;
//   - /teams-list/:season/:team     → `TeamsListDetail`: coach e giocatori.
// Forma a due livelli sul modello di ClinicEvents (riga-pulsante con caret e
// contatore → pagina di dettaglio su rotta, «Back» all'elenco).
//
// ⚠️ NESSUNA SCRITTURA. Le sole chiamate sono `GET /users` e
// `GET /teams?season=`, le stesse di Team Assignment. Nessuna persistenza di
// vista (niente storage): l'anno viaggia solo nell'indirizzo.
//
// LE REGOLE (decisioni del committente):
//   D1  contano e compaiono le SOLE tessere ACTIVE DELL'ANNO guardato
//       (`tessereDelSoggetto`, default `stati: ["ACTIVE"]`), mai lo status di
//       persona; tessere amministrative escluse (default del modulo);
//   D2  l'elenco è il CATALOGO dell'anno (`GET /teams?season=`), anche le
//       squadre a 0 giocatori;
//   D3  il coach con «All teams» non sta in nessuna squadra: per lui
//       `squadreDellaTessera` dà `teams: []`, e nessun nome gli corrisponde;
//   D5  una tessera → una riga nel suo gruppo, nessun caso speciale;
//   T2  nome catalogo ↔ tessera a confronto normalizzato (`eDiUnaMiaSquadra`).

import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { FaChevronRight } from "react-icons/fa";
import { api } from "../api/api";
import { useAuth } from "../context/AuthContext";
import { canonicalRole, normalizza } from "../utils/roleUtils";
import {
  eDiUnaMiaSquadra,
  squadreDellaTessera,
  tessereDelSoggetto
} from "../utils/squadre";
import { annoDiLista } from "../utils/stagione";
import { categoriaAnagrafica } from "../utils/categoria";
import { esportaGiocatori, nomeFilePulito } from "../utils/exportGiocatori";
import {
  idDellaCoorte,
  indicePerId,
  rigaGenitoriDiPersona
} from "../utils/rigaGenitori";
import HomeShell from "../components/HomeShell";
import SeasonStatusBar from "../components/SeasonStatusBar";
import { PastigliaCategoriaSeNota } from "../components/PastigliaCategoria";
import "../components/AdminTools.css";
import "./TeamsList.css";

/* =========================================================
   LA REGOLA CONDIVISA — chi sta in una squadra in un'annata
========================================================= */

/**
 * Coach e giocatori di UNA squadra in UN'annata, ciascuno una volta sola.
 * Si guardano le tessere ACTIVE dell'anno (D1, amministrative escluse), col
 * ruolo CANONICO (MANAGER/TD/MANAGERTD = COACH, CHILD = PLAYER); una persona
 * con più tessere dello stesso ruolo sulla stessa squadra conta una volta.
 *
 * ⚠️ CALCO DICHIARATO (cantiere TEAMS-LIST / L2). Il gemello è il filtro-rosa
 * di `GET /teams-export` in backend/server.js, che compone le righe dei
 * bottoni «Export». Regola comune d'appartenenza: una persona è GIOCATORE di
 * una squadra in un'annata se porta una tessera ACTIVE, di ruolo canonico
 * PLAYER (CHILD = PLAYER), con `cardSeason` di quell'anno, che nomina quella
 * squadra a confronto normalizzato; «All teams» non fa squadra; una riga per
 * squadra. Chi cambia l'una guardi l'altra: lo schermo e il file devono
 * contare le stesse persone.
 */
function membriDellaSquadra(users, nomeSquadra, anno) {
  const perRuolo = { COACH: new Map(), PLAYER: new Map() };

  users.forEach((u) => {
    const id = String((u && u.id) || "");
    if (!id) return;

    tessereDelSoggetto(u?.contexts, anno).forEach((ctx) => {
      const ruolo = canonicalRole(ctx?.role);
      if (!perRuolo[ruolo]) return;
      if (!eDiUnaMiaSquadra(nomeSquadra, squadreDellaTessera(ctx).teams)) return;
      perRuolo[ruolo].set(id, u);
    });
  });

  return {
    coaches: Array.from(perRuolo.COACH.values()),
    players: Array.from(perRuolo.PLAYER.values())
  };
}

/** Ordine alfabetico PER COGNOME (decisione del committente al passo 0: TA
 *  non ordina le persone). Metro della tendina squadre di TA —
 *  `normalizza(a).localeCompare(normalizza(b))` — sulla chiave «Cognome Nome». */
const perCognome = (a, b) =>
  normalizza(`${a?.lastName || ""} ${a?.firstName || ""}`).localeCompare(
    normalizza(`${b?.lastName || ""} ${b?.firstName || ""}`)
  );

/** Le squadre del catalogo, nella forma di `mappaTeams` di TA (solo il nome
 *  serve qui), in ordine alfabetico normalizzato come la tendina «Filter by
 *  team» di TA (decisione al passo 0). */
const squadreDelCatalogo = (dati) =>
  (Array.isArray(dati) ? dati : [])
    .filter(Boolean)
    .map((t) => (typeof t === "string" ? t : t.name))
    .filter((nome) => typeof nome === "string" && nome.trim() !== "")
    .sort((a, b) => normalizza(a).localeCompare(normalizza(b)));

/** `GET /users` una volta al montaggio (come TA). */
function useElencoUtenti() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function carica() {
      try {
        setLoading(true);
        const usersData = await api.get("/users");
        setUsers(Array.isArray(usersData) ? usersData : []);
      } catch (err) {
        setError("Failed to load data");
      } finally {
        setLoading(false);
      }
    }

    carica();
  }, []);

  return { users, loading, error };
}

/** L'EXPORT DEI GIOCATORI (L2), comune ai due bottoni: una chiamata a
 *  `GET /teams-export` (solo via `api`, col token) e il file .xlsx da
 *  utils/exportGiocatori.js. Bottone spento durante l'export; 0 righe →
 *  avviso, nessun file vuoto. */
function useExportGiocatori() {
  const [inCorso, setInCorso] = useState(false);
  const [avviso, setAvviso] = useState(null);

  async function esporta(percorso, anno, filename) {
    setInCorso(true);
    setAvviso(null);

    try {
      const dati = await api.get(percorso);
      const scritte = await esportaGiocatori(dati?.rows, anno, filename);
      if (scritte === 0) {
        setAvviso({ tipo: "hint", testo: "No players to export" });
      }
    } catch (err) {
      setAvviso({ tipo: "error", testo: "Export failed" });
    } finally {
      setInCorso(false);
    }
  }

  return { inCorso, avviso, esporta };
}

/** La riga d'avviso sotto il bottone, con le classi già della pagina. */
const AvvisoExport = ({ avviso }) =>
  avviso ? (
    <p className={avviso.tipo === "error" ? "tl-error" : "tl-hint"}>
      {avviso.testo}
    </p>
  ) : null;

/* =========================================================
   ELENCO — /teams-list
========================================================= */
export default function TeamsList() {
  const { operatingSeason, enrollmentSeason, seasonStatus } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const { users, loading: loadingUsers, error: erroreUtenti } = useElencoUtenti();
  const exportGiocatori = useExportGiocatori();

  // Il catalogo porta con sé l'ANNO per cui è stato letto: al cambio della
  // tendina, finché il nuovo non arriva, il vecchio non si conta sull'anno
  // nuovo (si mostra «Loading…»).
  const [catalogo, setCatalogo] = useState({ anno: null, squadre: [] });
  const [loadingTeams, setLoadingTeams] = useState(true);
  const [erroreSquadre, setErroreSquadre] = useState(null);

  // L'anno SCELTO nella tendina: `undefined` = nessuna scelta (stesso pattern
  // di TA, nessun effetto che lo scriva).
  const [stagioneScelta, setStagioneScelta] = useState(undefined);

  // L'anno dell'INDIRIZZO (`?season=`), col quale torna qui il «Back» del
  // dettaglio. Lettura: `useLocation()` (come CoachAttendanceDetail) +
  // `new URLSearchParams(…).get(…)` (come hooks/useFCMDeviceToken.js) —
  // composizione decisa dal committente al passo 0.
  const annoDaIndirizzo = annoDiLista(
    new URLSearchParams(location.search).get("season")
  );

  /* LE ANNATE SCEGLIBILI E IL DEFAULT — COPIATI da TeamAssignment.jsx
     (`annateDeiDati`, `stagioniDisponibili`, `stagionePredefinita`,
     `stagioneAttiva`), che a sua volta li copia dalla User List. Chi cambia
     l'impianto là deve guardare anche qui. */
  const annateDeiDati = useMemo(
    () =>
      Array.from(
        users.reduce((elenco, u) => {
          (Array.isArray(u?.contexts) ? u.contexts : []).forEach((ctx) => {
            const anno = annoDiLista(ctx?.cardSeason);
            if (anno !== null) elenco.add(anno);
          });
          (Array.isArray(u?.seasons) ? u.seasons : []).forEach((voce) => {
            const anno = annoDiLista(voce);
            if (anno !== null) elenco.add(anno);
          });
          const anno = annoDiLista(u?.season);
          if (anno !== null) elenco.add(anno);
          return elenco;
        }, new Set())
      ).sort((a, b) => b - a),
    [users]
  );

  const stagioniDisponibili = useMemo(
    () =>
      Array.from(
        [operatingSeason, enrollmentSeason].reduce(
          (elenco, anno) => (anno == null ? elenco : elenco.add(anno)),
          new Set(annateDeiDati)
        )
      ).sort((a, b) => b - a),
    [annateDeiDati, operatingSeason, enrollmentSeason]
  );

  const stagionePredefinita =
    operatingSeason != null
      ? operatingSeason
      : seasonStatus === "error"
        ? (annateDeiDati.length > 0 ? annateDeiDati[0] : null)
        : null;

  // ⚠️ `?season=` VALE SOLO SE È FRA LE ANNATE SCEGLIBILI: un anno che la
  // tendina non offre non si può mostrare selezionato. Altrimenti il default.
  const annoInizialeValido =
    annoDaIndirizzo !== null && stagioniDisponibili.includes(annoDaIndirizzo);

  const stagioneAttiva =
    stagioneScelta !== undefined
      ? stagioneScelta
      : annoInizialeValido
        ? annoDaIndirizzo
        : stagionePredefinita;

  // Il catalogo dell'anno (D2). ⚠️ Si chiede solo con un anno INTERO e a
  // utenti caricati: senza anno la regola delle tessere non si applica
  // (`tesseraDellAnno` con `null` fa passare tutto) e i conteggi
  // mescolerebbero le annate; e prima degli utenti l'anno dell'indirizzo non
  // si può ancora convalidare.
  useEffect(() => {
    if (loadingUsers || !Number.isInteger(stagioneAttiva)) return;

    let annullato = false;

    async function caricaSquadre() {
      try {
        setLoadingTeams(true);
        setErroreSquadre(null);
        const teamsData = await api.get(
          `/teams?season=${encodeURIComponent(stagioneAttiva)}`
        );
        if (!annullato) {
          setCatalogo({
            anno: stagioneAttiva,
            squadre: squadreDelCatalogo(teamsData)
          });
        }
      } catch (err) {
        if (!annullato) setErroreSquadre("Failed to load data");
      } finally {
        if (!annullato) setLoadingTeams(false);
      }
    }

    caricaSquadre();

    return () => {
      annullato = true;
    };
  }, [loadingUsers, stagioneAttiva]);

  const righe = useMemo(
    () =>
      Number.isInteger(stagioneAttiva) && catalogo.anno === stagioneAttiva
        ? catalogo.squadre.map((nome) => {
            const { players } = membriDellaSquadra(users, nome, stagioneAttiva);
            return {
              nome,
              giocatori: players.length,
              idGiocatori: players.map((u) => String(u.id))
            };
          })
        : [],
    [catalogo, users, stagioneAttiva]
  );

  // I TOTALI DI STAGIONE (cantiere TEAMS-LIST / L1): le squadre del catalogo
  // dell'anno, anche a 0 giocatori (D2), e i giocatori come PERSONE DISTINTE
  // sull'unione delle squadre — chi gioca in due squadre conta una volta.
  // Stessi id PLAYER di `membriDellaSquadra`, nessuna regola nuova.
  const totali = useMemo(
    () => ({
      squadre: righe.length,
      giocatori: new Set(righe.flatMap((r) => r.idGiocatori)).size
    }),
    [righe]
  );

  const errore = erroreUtenti || erroreSquadre;
  const inCaricamento =
    loadingUsers ||
    loadingTeams ||
    !Number.isInteger(stagioneAttiva) ||
    catalogo.anno !== stagioneAttiva;

  return (
    <HomeShell backTo="/home" width="admin">
      <h1 className="qpsc-page-title">Teams</h1>
      <p className="qpsc-note tl-centered">Players and coaches by team</p>

      {/* Nuda, come in TA: questa pagina la stagione la LEGGE. Figlia diretta
          della colonna del telaio, fuori da ogni griglia. */}
      <SeasonStatusBar />

      <section className="tl-page">
        {/* «SEASON VIEWED» — tendina di TA, stessi testi e stessa
            conversione (stringa nel DOM, numero nello stato). */}
        <div className="qpsc-field tl-season">
          <span className="qpsc-field__label">Season Viewed</span>
          <select
            className="qpsc-select"
            aria-label="Season viewed"
            value={stagioneAttiva === null ? "" : String(stagioneAttiva)}
            onChange={(e) => setStagioneScelta(Number(e.target.value))}
          >
            {stagioniDisponibili.map((anno) => (
              <option key={anno} value={String(anno)}>{anno}</option>
            ))}
          </select>
        </div>

        {errore && <p className="tl-error">{errore}</p>}

        {!errore && inCaricamento && <p className="tl-hint">Loading…</p>}

        {!errore && !inCaricamento && righe.length === 0 && (
          <p className="tl-hint">No teams for {stagioneAttiva}.</p>
        )}

        {!errore && !inCaricamento && righe.length > 0 && (
          <>
            <p className="tl-totals">
              {[
                `${totali.squadre} ${totali.squadre === 1 ? "team" : "teams"}`,
                `${totali.giocatori} ${totali.giocatori === 1 ? "player" : "players"}`
              ].join(" · ")}
            </p>
            {/* EXPORT DI STAGIONE (L2): solo con righe a schermo, cioè mai
                durante «Loading…», su errore o a catalogo vuoto. Stile del
                «Export CSV» della User List. */}
            <div className="tl-export">
              <button
                type="button"
                className="qpsc-ghost-btn qpsc-ghost-btn--wide"
                disabled={exportGiocatori.inCorso}
                onClick={() =>
                  exportGiocatori.esporta(
                    `/teams-export?season=${encodeURIComponent(stagioneAttiva)}`,
                    stagioneAttiva,
                    `QPSC_players_${stagioneAttiva}.xlsx`
                  )
                }
              >
                {exportGiocatori.inCorso ? "Exporting…" : "Export all players"}
              </button>
              <AvvisoExport avviso={exportGiocatori.avviso} />
            </div>
            <ul className="tl-list">
              {righe.map(({ nome, giocatori }) => (
                <li key={nome} className="tl-item">
                  {/* Riga-pulsante sul modello di ClinicEvents: tutta la riga
                      apre il dettaglio, raggiungibile da tastiera. */}
                  <button
                    type="button"
                    className="tl-row"
                    onClick={() =>
                      navigate(
                        `/teams-list/${encodeURIComponent(stagioneAttiva)}/${encodeURIComponent(nome)}`
                      )
                    }
                  >
                    <FaChevronRight className="tl-row__caret" aria-hidden="true" />
                    <span className="tl-row__title">
                      {nome}{" "}
                      <span className="tl-row__count">
                        — {giocatori} {giocatori === 1 ? "player" : "players"}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </section>
    </HomeShell>
  );
}

/* =========================================================
   DETTAGLIO — /teams-list/:season/:team
========================================================= */
export function TeamsListDetail() {
  const { season, team } = useParams();
  const navigate = useNavigate();

  const anno = annoDiLista(season);
  const nomeSquadra = String(team || "").trim();

  const { users, loading, error } = useElencoUtenti();
  const exportGiocatori = useExportGiocatori();

  const membri = useMemo(() => {
    if (anno === null || !nomeSquadra) return { coaches: [], players: [] };
    const { coaches, players } = membriDellaSquadra(users, nomeSquadra, anno);
    return {
      coaches: coaches.slice().sort(perCognome),
      players: players.slice().sort(perCognome)
    };
  }, [users, nomeSquadra, anno]);

  // La riga genitori (D6/D7): indice su TUTTO `users`, coorte dell'anno con
  // la definizione di TA (utils/rigaGenitori.js, calco dichiarato).
  const indiceDiPersona = useMemo(() => indicePerId(users), [users]);
  const idInCoorte = useMemo(
    () => (anno === null ? new Set() : idDellaCoorte(users, anno)),
    [users, anno]
  );

  // Un indirizzo senza anno leggibile o senza squadra non ha niente da
  // mostrare: si torna all'elenco (Navigate, come il catch-all di App.jsx).
  if (anno === null || !nomeSquadra) {
    return <Navigate to="/teams-list" replace />;
  }

  const gruppi = [
    { chiave: "coaches", titolo: "Coaches", vuoto: "No coaches", persone: membri.coaches },
    { chiave: "players", titolo: "Players", vuoto: "No players", persone: membri.players }
  ];

  return (
    <HomeShell
      backTo={`/teams-list?season=${encodeURIComponent(anno)}`}
      width="admin"
    >
      <h1 className="qpsc-page-title">{nomeSquadra} — {anno}</h1>

      <section className="tl-page">
        {error && <p className="tl-error">{error}</p>}

        {!error && loading && <p className="tl-hint">Loading…</p>}

        {/* EXPORT DI SQUADRA (L2), in testa alla pagina sotto il titolo. */}
        {!error && !loading && (
          <div className="tl-export">
            <button
              type="button"
              className="qpsc-ghost-btn qpsc-ghost-btn--wide"
              disabled={exportGiocatori.inCorso}
              onClick={() =>
                exportGiocatori.esporta(
                  `/teams-export?season=${encodeURIComponent(anno)}&team=${encodeURIComponent(nomeSquadra)}`,
                  anno,
                  `QPSC_players_${nomeFilePulito(nomeSquadra)}_${anno}.xlsx`
                )
              }
            >
              {exportGiocatori.inCorso ? "Exporting…" : "Export players"}
            </button>
            <AvvisoExport avviso={exportGiocatori.avviso} />
          </div>
        )}

        {!error && !loading &&
          gruppi.map((g) => (
            <div key={g.chiave} className="tl-group">
              <div className="tl-group__label">
                {g.titolo} ({g.persone.length})
              </div>

              {g.persone.length === 0 ? (
                <p className="tl-hint">{g.vuoto}</p>
              ) : (
                <ul className="tl-list">
                  {g.persone.map((u) => {
                    const genitori = rigaGenitoriDiPersona(
                      u,
                      indiceDiPersona,
                      idInCoorte
                    );

                    return (
                      <li key={u.id} className="tl-item tl-person">
                        <div className="tl-person__ident">
                          <span className="tl-person__name">
                            {u.firstName} {u.lastName}
                          </span>
                          {genitori && (
                            <span className="tl-person__family">{genitori}</span>
                          )}
                        </div>

                        {/* Pastiglia di TA: la categoria anagrafica
                            dell'annata guardata; tace se non nota. */}
                        <div className="tl-person__gender">
                          <PastigliaCategoriaSeNota
                            category={categoriaAnagrafica(u, anno)}
                          />
                        </div>

                        {/* View Profile come in TA (§5.4). */}
                        <button
                          className="qpsc-profile-btn tl-person__profile"
                          onClick={() => navigate(`/profile/${u.id}`)}
                        >
                          View Profile
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          ))}
      </section>
    </HomeShell>
  );
}
